'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const CONSENT_STORAGE_KEY = 'musein:cookie_consent';

type ConsentChoice = 'accepted' | 'rejected';
type ConsentSnapshot = ConsentChoice | 'unset' | 'unknown';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface CookieConsentBannerProps {
  enabled: boolean;
}

function updateGoogleConsent(choice: ConsentChoice) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagFallback(...args: unknown[]) {
    window.dataLayer?.push(args as unknown as Record<string, unknown>);
  };

  const granted = choice === 'accepted' ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: granted,
    analytics_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
  });
}

function getStoredConsent(): ConsentChoice | null {
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

function storeConsent(choice: ConsentChoice) {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Consent still applies for this page even if storage is unavailable.
  }

  window.dispatchEvent(new Event('musein-cookie-consent-change'));
}

function subscribeToConsent(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener('storage', callback);
  window.addEventListener('musein-cookie-consent-change', callback);

  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener('musein-cookie-consent-change', callback);
  };
}

function getConsentSnapshot(): ConsentSnapshot {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return getStoredConsent() ?? 'unset';
}

export function CookieConsentBanner({ enabled }: CookieConsentBannerProps) {
  const consentSnapshot = useSyncExternalStore(
    subscribeToConsent,
    getConsentSnapshot,
    () => 'unknown',
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (consentSnapshot === 'accepted' || consentSnapshot === 'rejected') {
      updateGoogleConsent(consentSnapshot);
    }

  }, [consentSnapshot, enabled]);

  const handleChoice = (nextChoice: ConsentChoice) => {
    storeConsent(nextChoice);
    updateGoogleConsent(nextChoice);

  };

  if (!enabled || consentSnapshot !== 'unset') {
    return null;
  }

  return (
    <section
      aria-label="Cookie consent"
      className={cn(
        'fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-50 mx-auto max-w-3xl rounded-2xl border border-zinc-200 bg-white p-4 text-zinc-950 shadow-lg',
        'dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50',
      )}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-balance text-sm font-semibold">Analytics and advertising cookies</h2>
          <p className="text-pretty text-xs leading-5 text-zinc-600 dark:text-zinc-400">
            Musein uses analytics and advertising cookies to measure campaigns, improve product flows, and support
            enhanced conversion reporting. You can reject non-essential tracking.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleChoice('rejected')}
          >
            Reject
          </Button>
          <Button
            type="button"
            color="primary"
            size="sm"
            onClick={() => handleChoice('accepted')}
          >
            Accept
          </Button>
        </div>
      </div>
    </section>
  );
}
